import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { toast } from '@doxyme/ui/components/foundations';
import { _getLogger } from '@doxyme/analytics';
import { isProduction } from '../../../shared/isProduction';
import {
  processSideloadExtension,
  parseSideloadParam,
  getSideloadParamsFromHash,
  SIDELOAD_PARAM_KEY,
  setSideloadBaseUrl,
} from '../../../features/extensions/sideloading';
import { syncSideloadedExtensionsToMembers } from '../../../features/extensions/sideloading/syncSideloadedExtensionsToMembers';
import { useAppDispatch } from '../../useReduxToolkit';

const logger = _getLogger('useExtensionsSideloading', {
  component: 'extensions',
});

/**
 * Gets sideload params from hash fragment or query string.
 * Hash params take precedence over query params to avoid server URL length limits.
 *
 * @example ['http%3A%2F%2Flocalhost%3A4704%2F%3B%7B%22name%22%3A%22%40doxyme%2Ffax%22%2C%22title%22%3A%22Fax%22...']
 */
function getSideloadParams(
  searchParams: ReturnType<typeof useSearchParams>,
): string[] {
  const hashParams = getSideloadParamsFromHash();
  return hashParams.length > 0
    ? hashParams
    : searchParams.getAll(SIDELOAD_PARAM_KEY);
}

export interface UseExtensionsSideloadingResult {
  sideloadedNames: Set<string>;
  isComplete: boolean;
}

/**
 * Hook that handles sideloading extensions via URL parameters.
 *
 * Supports both query string and hash fragment (hash takes precedence):
 * - Hash fragment: `#sideloadExtension=baseUrl;encodedManifestJson` (preferred)
 * - Query string: `?sideloadExtension=baseUrl;encodedManifestJson` (fallback)
 *
 * Hash fragment is recommended for long URLs as it avoids server-side URL length limits
 * (e.g., AWS CloudFront) since the fragment is never sent to the server.
 *
 * Completely disabled on production for security.
 *
 * @returns Set of sideloaded extension names and completion status
 */
export function useExtensionsSideloading(): UseExtensionsSideloadingResult {
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const [sideloadedNames, setSideloadedNames] = useState<Set<string>>(
    new Set(),
  );
  const [isComplete, setIsComplete] = useState(false);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Only process once
    if (hasProcessedRef.current) {
      return;
    }
    hasProcessedRef.current = true;

    // Disabled on production for security
    if (isProduction()) {
      setIsComplete(true);
      logger.warn(
        '[useExtensionsSideloading] Sideloading is disabled on production',
      );
      return;
    }

    const sideloadParams = getSideloadParams(searchParams);

    if (sideloadParams.length === 0) {
      setIsComplete(true);
      return;
    }

    logger.info('[useExtensionsSideloading] Processing sideload params', {
      count: sideloadParams.length,
    });

    const names = new Set<string>();

    for (const param of sideloadParams) {
      const parsed = parseSideloadParam(param);
      if (!parsed) {
        logger.error(
          '[useExtensionsSideloading] Failed to parse sideload param',
          { param },
        );
        toast.error({
          title: 'Failed to sideload extension. Check the console for details.',
        });
        continue;
      }

      try {
        const result = processSideloadExtension({
          baseUrl: parsed.baseUrl,
          manifest: parsed.manifest,
        });
        names.add(result.name);
        setSideloadBaseUrl(parsed.manifest.name, parsed.baseUrl);
        void dispatch(syncSideloadedExtensionsToMembers());
        toast.success({
          title: `Sideloaded: ${result.name}`,
        });
      } catch (error) {
        logger.error('[useExtensionsSideloading] Failed to process sideload', {
          error,
          manifestName: parsed.manifest.name,
        });
        toast.error({
          title: `Failed to sideload: ${parsed.manifest.name}. Check the console.`,
        });
      }
    }

    setSideloadedNames(names);
    setIsComplete(true);
  }, [searchParams]);

  return { sideloadedNames, isComplete };
}
